/**
 * Celebration context — drives the onboarding completion takeover.
 *
 * The `trigger_celebration` tool fires `show(payload)`; the Thread component
 * reads `active` to swap its content for a full-widget celebration screen.
 * The screen auto-dismisses after CONFETTI_LIFE_MS + 10s (≈15.2s), or earlier
 * via the close button or a CTA click.
 */

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
  type ReactNode,
} from 'react';

export interface CelebrationCTA {
  label: string;
  url: string;
  primary: boolean;
}

export interface CelebrationPayload {
  headline: string;
  subtitle: string;
  bullets: string[];
  ctas: CelebrationCTA[];
}

interface CelebrationState extends Partial<CelebrationPayload> {
  active: boolean;
}

interface CelebrationContextValue extends CelebrationState {
  show: (payload: CelebrationPayload) => void;
  dismiss: () => void;
}

const EMPTY_STATE: CelebrationState = { active: false };

const CelebrationContext = createContext<CelebrationContextValue>({
  ...EMPTY_STATE,
  show: () => {},
  dismiss: () => {},
});

// Confetti animation life = 4s + max 1.2s stagger delay (see fullscreenConfettiFall
// in src/index.css). Keep the screen up for 10s after that finishes.
const CONFETTI_LIFE_MS = 5_200;
const POST_CONFETTI_HOLD_MS = 10_000;
const AUTO_DISMISS_MS = CONFETTI_LIFE_MS + POST_CONFETTI_HOLD_MS;

export function CelebrationProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<CelebrationState>(EMPTY_STATE);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const clearTimer = useCallback(() => {
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
  }, []);

  const dismiss = useCallback(() => {
    clearTimer();
    setState(EMPTY_STATE);
  }, [clearTimer]);

  const show = useCallback(
    (payload: CelebrationPayload) => {
      clearTimer();
      setState({ active: true, ...payload });
      // Ask the host page to fire its own confetti burst layered above the
      // iframe — embed.js handles GAIA_EMBED_CONFETTI. Safe to call in the
      // standalone (non-embedded) case: window.parent === window, the message
      // bounces back and is ignored.
      window.parent.postMessage({ type: 'GAIA_EMBED_CONFETTI' }, '*');
      timerRef.current = setTimeout(() => {
        timerRef.current = null;
        setState(EMPTY_STATE);
      }, AUTO_DISMISS_MS);
    },
    [clearTimer],
  );

  useEffect(() => clearTimer, [clearTimer]);

  return (
    <CelebrationContext.Provider value={{ ...state, show, dismiss }}>
      {children}
    </CelebrationContext.Provider>
  );
}

export function useCelebration() {
  return useContext(CelebrationContext);
}
